
import Vue from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import { SNACKBAR_STATUS } from '@/constants/constants';
import {
	TeamUserRolePermission,
	TeamUserRolePermissionGroup,
	TEAM_USER_ROLE_PERMISSION_DATA,
	TEAM_USER_ROLE_PERMISSION,
	TEAM_USER_ROLE_PERMISSION_GROUP,
	TeamUserRoleDef,
	TEAM_USER_ROLE_PERMISSION_GROUP_LABEL,
	hundredthsToTokens,
	tokensToHundredths,
	TOKEN_COOLDOWN_TIME_BUCKET,
	TokenCooldownTimeBucket,
	RUNNIT_NODE_DEF_TAG_TYPE,
	RunnitNodeDefTag,
	PLUGIN_KIND,
	PLUGIN_KIND_LABEL,
	PluginKind,
	currencyTokens,
	normalizeMediaTypeMaxes,
	resolveRunnitNodeDefTagIcon,
} from '@run-diffusion/shared';
import _cloneDeep from 'lodash/cloneDeep';
import _isEmpty from 'lodash/isEmpty';
import EmptyState from '@/components/states/EmptyState.vue';
import { ROUTER } from '@/router/constants';
import { titleCase } from '@/filters';
import pluralize from 'pluralize';
import BackButton from '@/components/base/BackButton.vue';
import BaseBreadcrumbs from '@/components/base/BaseBreadcrumbs.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import EnterpriseUpsellBadge from '@/components/EnterpriseUpsellWrapper/EnterpriseUpsellBadge.vue';
import ColorInput from '@/components/ColorInput.vue';
import DoubleConfirmDangerDialog from '@/components/base/DoubleConfirmDangerDialog.vue';
import { db, functions } from '@/firebase';
import { get$bindFirestoreOptions } from '@/mixins/VuexFirestoreBindingsMixin';
import { RunnitsAndRunnitNodeDefsBindingsMixin } from '@/mixins';
import { TeamUserMixin } from '@/mixins/TeamUserMixin';
import NumberWithIncrementer from '@/components/base/NumberWithIncrementer.vue';
import NumberField from '@/components/base/NumberField.vue';
import BaseGreyToggleButton from '@/components/base/BaseGreyToggleButton.vue';
import TokenCooldownTimeBucketPicker from '@/components/base/TokenCooldownTimeBucketPicker.vue';
import RoleDefBadge from '@/views/Team/CompanyDashboard/RoleDefBadge.vue';
import RunnitToolsListSelectByTag from '@/views/Runnits/RunnitToolsListSelectByTag.vue';
import BaseToggle from '@/components/base/BaseToggle.vue';

// Plugin-kind → logo asset map; uses the same art as PluginsApiPage so the admin role editor and the public plugins surface stay visually in sync. Rhino is McNeel's version-agnostic vector mark, so it does not need revisiting when Rhino 9 ships.
const PLUGIN_KIND_LOGOS: Record<PluginKind, string> = {
	[PLUGIN_KIND.PHOTOSHOP]: require('@/assets/plugins/photoshop.svg'),
	[PLUGIN_KIND.MIRO]: require('@/assets/plugins/miro.svg'),
	[PLUGIN_KIND.REVIT]: require('@/assets/plugins/revit.svg'),
	[PLUGIN_KIND.RHINO]: require('@/assets/plugins/rhino.svg'),
	// Placeholder art, not NVIDIA's mark. See the note in the SVG itself.
	[PLUGIN_KIND.OMNIVERSE]: require('@/assets/plugins/omniverse.svg'),
};

// Plugin kinds whose plugin has actually shipped and can be granted here. Others render disabled with an "(in development)" hint until their plugin lands.
const AVAILABLE_PLUGIN_KINDS: Record<PluginKind, boolean> = {
	[PLUGIN_KIND.PHOTOSHOP]: true,
	[PLUGIN_KIND.MIRO]: true,
	[PLUGIN_KIND.REVIT]: true,
	[PLUGIN_KIND.RHINO]: true,
	[PLUGIN_KIND.OMNIVERSE]: true,
};

// Kinds a RunDiffusion admin (user.isAdmin) may grant BEFORE the plugin is public, so we
// can dogfood against production without customers finding an unreleased plugin. This is
// a UI affordance only: the server-side gate is pluginAccessSettings.pluginKinds either
// way, so a kind granted here is genuinely enabled rather than faked client-side.
// Delete a kind from here once it graduates into AVAILABLE_PLUGIN_KINDS.
//
// Empty today: every plugin kind is public. Kept rather than removed because it is the
// mechanism the next unreleased plugin uses, and the alternative (granting a kind nobody
// can install yet, or shipping no way to dogfood at all) is worse in both directions.
const RD_ADMIN_ONLY_PLUGIN_KINDS: Partial<Record<PluginKind, boolean>> = {};

export default Vue.extend({
	name: 'ManageTeamRolesPage',
	metaInfo () {
		return {
			title: `${this.displayLabels.team} Roles | ${this.team?.company?.name || 'Company'} | RunDiffusion`,
		};
	},
	data () {
		return {
			PLUGIN_KIND,
			TEAM_USER_ROLE_PERMISSION_DATA,
			TEAM_USER_ROLE_PERMISSION_GROUP_LABEL,
			RUNNIT_NODE_DEF_TAG_TYPE,

			availableRoleDefs: [] as TeamUserRoleDef[],
			loadingAvailableRoleDefs: false,

			selectedRoleDef: null as TeamUserRoleDef | null,
			originalRoleDef: null as TeamUserRoleDef | null,
			editingRoleDef: null as TeamUserRoleDef | null,
			isEditMode: false,

			saving: false,

			// Which token-limit media mode the radio shows WHILE EDITING. Null means
			// "derive it from what's stored", which is what a freshly loaded role
			// wants. Held separately because a role can legitimately sit in per-media
			// mode with every row switched off, and a purely derived mode would snap
			// the radio back to master the moment the last row came off.
			tokenLimitMediaModeSelection: null as 'master' | 'perMedia' | null,

			// delete role dialog state
			deleteConfirmDialogOpen: false,
			deletingRole: false,
		};
	},
	mounted () {
		this.bindTeamUserRoleDefs();
	},
	destroyed () {
		if (this.$firestoreRefs && this.$firestoreRefs.availableRoleDefs) {
			this.$unbind('availableRoleDefs');
		}
	},
	mixins: [
		TeamUserMixin,
		RunnitsAndRunnitNodeDefsBindingsMixin,
	],
	computed: {
		...mapState([
			'user',
			'team',
			'publicRunnitNodeDefTags',
		]),
		...mapGetters([
			'displayLabels',
			'companyClubOfferBenefits',
		]),
		tokenLimitApplyToToggleOptions () {
			return [
				{ value: false, label: 'per result' },
				{ value: true, label: 'total batch' },
			];
		},
		/**
		 * The MEDIA tool tags, in the order an admin arranged them in Manage Tool
		 * Tags. Data-driven on purpose: creating a new MEDIA tag makes it
		 * customizable here with no code change.
		 */
		mediaTypeOptions (): { id: string; label: string; icon: string }[] {
			return ((this.publicRunnitNodeDefTags || []) as RunnitNodeDefTag[])
				.filter((tag: RunnitNodeDefTag) => tag && tag.type === RUNNIT_NODE_DEF_TAG_TYPE.MEDIA && !tag.isDeleted)
				.slice()
				.sort((a: RunnitNodeDefTag, b: RunnitNodeDefTag) => (a.sortOrder || 0) - (b.sortOrder || 0))
				.map((tag: RunnitNodeDefTag) => ({
					id: tag.id,
					label: tag.label,
					// The tag's own icon, else its type's, else a generic one. Keyed off
					// the tag document rather than its label, so renaming Video in
					// Manage Tool Tags cannot silently drop its icon.
					icon: resolveRunnitNodeDefTagIcon(tag),
				}));
		},
		mediaTypeTagIds (): string[] {
			return this.mediaTypeOptions.map((option: { id: string }) => option.id);
		},
		/** Which token-limit mode the radio reflects: the single master cap vs. per-media caps. */
		tokenLimitMediaMode (): 'master' | 'perMedia' {
			if (this.tokenLimitMediaModeSelection) return this.tokenLimitMediaModeSelection;
			return _isEmpty(this.normalizeTokenLimitMediaTypeMaxes(this.editingRoleDef)) ? 'master' : 'perMedia';
		},
		/**
		 * True when TOKEN_LIMIT is switched on but the master cap is blank or 0.
		 *
		 * That combination is not a permissive role, it is a BROKEN one: the run
		 * paths treat a TOKEN_LIMIT role with no usable cap as misconfigured and
		 * refuse every generation, including for media types that DO have their
		 * own number, since those only override the master for their own tools.
		 * So this blocks saving rather than warning.
		 *
		 * Only enforced while the permission is active and reachable: a role with
		 * TOKEN_LIMIT off, or with it disabled because GENERATE_RUNNIT is off, has
		 * nothing to validate.
		 */
		tokenLimitMasterMissing (): boolean {
			if (!this.editingRoleDef?.permissions?.[TEAM_USER_ROLE_PERMISSION.TOKEN_LIMIT]) return false;
			if (this.isPermissionDisabled(TEAM_USER_ROLE_PERMISSION.TOKEN_LIMIT)) return false;
			const max: number = this.editingRoleDef?.tokenLimitSettings?.max;
			return !(typeof max === 'number' && Number.isFinite(max) && max > 0);
		},
		/** Rule for the master cap field, so the reason shows on the field itself. */
		tokenLimitMaxRules (): ((v: unknown) => true | string)[] {
			return [
				(v: unknown) => {
					const n: number = Number(v);
					if (Number.isFinite(n) && n > 0) return true;
					return 'Must be more than 0';
				},
			];
		},
		/** What a media type with no custom limit falls back to, spelled out on its row. */
		masterTokenLimitHint (): string {
			const max: number = this.editingRoleDef?.tokenLimitSettings?.max;
			if (typeof max !== 'number' || !(max > 0)) return 'Uses the limit set above';
			return `Uses the limit set above (${currencyTokens(hundredthsToTokens(max))} tokens)`;
		},
		/** The OAuth device flow section of the public API docs. */
		publicApiDocsUrl (): string {
			return 'https://www.rundiffusion.com/docs/api/authentication#oauth-device-flow';
		},
		pluginKindOptions () {
			// Order is the public PLUGIN_KIND enum order; labels come from the shared label map so the admin UI and any future surface (e.g. the plugin's overlay) stay in sync.
			const isRdAdmin: boolean = !!(this.user && this.user.isAdmin);
			return Object.values(PLUGIN_KIND).map((value: PluginKind) => {
				const isPublic: boolean = !!AVAILABLE_PLUGIN_KINDS[value];
				// Unreleased kinds stay togglable for us and only for us, so the row reads
				// "(not public yet)" rather than "(in development)": we can act on it, and the
				// label should not imply otherwise.
				const adminOnly: boolean = !isPublic && isRdAdmin && !!RD_ADMIN_ONLY_PLUGIN_KINDS[value];
				return {
					value,
					label: PLUGIN_KIND_LABEL[value],
					logo: PLUGIN_KIND_LOGOS[value],
					available: isPublic || adminOnly,
					adminOnly,
				};
			});
		},
		// Which plugin-access mode the USE_PLUGINS radio reflects: opt-in-all vs. explicit list.
		pluginAccessMode (): 'all' | 'explicit' {
			return this.editingRoleDef?.pluginAccessSettings?.optInAll ? 'all' : 'explicit';
		},
		// Team roles are assigned on each team's own dashboard, opened from the teams
		// table on the Company Dashboard; this page only defines them. The table is
		// named as its own heading names it.
		teamsLabel (): string {
			return `Active ${titleCase(pluralize(this.displayLabels.team))}`;
		},
		companyDashboardRoute () {
			return { name: ROUTER.COMPANY_DASHBOARD };
		},
		breadcrumbs () {
			return [
				{
					hoverColor: 'var(--primary-purple)',
					text: 'Company Dashboard',
					disabled: false,
					to: {
						name: ROUTER.COMPANY_DASHBOARD,
					},
				},
				{
					hoverColor: 'var(--primary-blue)',
					text: 'Company Settings',
					disabled: false,
					to: {
						name: ROUTER.COMPANY_SETTINGS,
					},
				},
				{
					hoverColor: 'var(--primary-blue)',
					text: `${this.displayLabels['team']} Roles`,
					disabled: true,
					to: {
						name: ROUTER.TEAM_ROLES,
					},
				},
			];
		},
		permissionTypes (): TeamUserRolePermissionGroup[] {
			return Object.values(TEAM_USER_ROLE_PERMISSION_GROUP) as TeamUserRolePermissionGroup[];
		},
		filteredPermissionTypes () {
			return this.permissionTypes.filter(group => (this.permissionsByGroup[group] || []).length > 0);
		},
		permissionsByGroup () {
			return Object.keys(TEAM_USER_ROLE_PERMISSION_DATA(this.displayLabels)).reduce((acc, key: TeamUserRolePermission) => {
				const permission = TEAM_USER_ROLE_PERMISSION_DATA(this.displayLabels)[key];
				acc[permission.group] = acc[permission.group] || [];
				acc[permission.group].push(key);
				return acc;
			}, {} as Record<TeamUserRolePermissionGroup, TeamUserRolePermission[]>);
		},
		hasUnsavedChanges () {
			if (!this.editingRoleDef || !this.originalRoleDef) return false;

			// Normalize permissions by comparing only keys that are true
			const getTruePermissionKeys = (perms?: Record<string, boolean>) => {
				if (!perms || Object.keys(perms).length === 0) return [] as string[];
				return Object.keys(perms)
					.filter((key) => Boolean(perms[key]))
					.sort();
			};

			const originalTruePermissions = getTruePermissionKeys(this.originalRoleDef.permissions);
			const editingTruePermissions = getTruePermissionKeys(this.editingRoleDef.permissions);
			const permissionsChanged = JSON.stringify(originalTruePermissions) !== JSON.stringify(editingTruePermissions);

			return (
				this.editingRoleDef.name !== this.originalRoleDef.name ||
				this.editingRoleDef.description !== this.originalRoleDef.description ||
				this.editingRoleDef.color !== this.originalRoleDef.color ||
				this.editingRoleDef.tokenLimitSettings?.max !== this.originalRoleDef.tokenLimitSettings?.max ||
				this.editingRoleDef.tokenLimitSettings?.applyToTotalRun !== this.originalRoleDef.tokenLimitSettings?.applyToTotalRun ||
				JSON.stringify(this.normalizeTokenLimitMediaTypeMaxes(this.editingRoleDef)) !== JSON.stringify(this.normalizeTokenLimitMediaTypeMaxes(this.originalRoleDef)) ||
				this.editingRoleDef.tokenCooldownSettings?.tokens !== this.originalRoleDef.tokenCooldownSettings?.tokens ||
				this.editingRoleDef.tokenCooldownSettings?.timeBucket !== this.originalRoleDef.tokenCooldownSettings?.timeBucket ||
				this.editingRoleDef.numResultsLimitSettings?.max !== this.originalRoleDef.numResultsLimitSettings?.max ||
				this.editingRoleDef.level !== this.originalRoleDef.level ||
				JSON.stringify(this.normalizeRestrictNodeDefTagsPayload(this.editingRoleDef)) !== JSON.stringify(this.normalizeRestrictNodeDefTagsPayload(this.originalRoleDef)) ||
				JSON.stringify(this.normalizePluginAccessPayload(this.editingRoleDef)) !== JSON.stringify(this.normalizePluginAccessPayload(this.originalRoleDef)) ||
				permissionsChanged
			);
		},
		lowestRoleDef (): TeamUserRoleDef | null {
			if (!(this.availableRoleDefs || []).length) return null;
			return this.availableRoleDefs[this.availableRoleDefs.length - 1] as TeamUserRoleDef;
		},
		lowestRoleLabel (): string {
			return (this.lowestRoleDef && this.lowestRoleDef.name) ? this.lowestRoleDef.name : '';
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
			'setUpsellDialog',
		]),
		hundredthsToTokens,
		normalizeRestrictNodeDefTagIds (roleDef: TeamUserRoleDef | null): string[] {
			const tags: Record<string, true> = roleDef?.restrictNodeDefTagsSettings?.tags;
			if (!tags) return [];
			return Object.keys(tags)
				.filter((id: string) => tags[id] === true)
				.sort();
		},
		normalizeRestrictNodeDefIds (roleDef: TeamUserRoleDef | null): string[] {
			const ids: string[] = this._get(roleDef, 'restrictNodeDefTagsSettings.nodeDefIds') || [];
			return Array.from(new Set(ids.filter((id: string) => id))).sort();
		},
		normalizeRestrictNodeDefTagsPayload (roleDef: TeamUserRoleDef | null): TeamUserRoleDef['restrictNodeDefTagsSettings'] {
			const tagIds: string[] = this.normalizeRestrictNodeDefTagIds(roleDef);
			const nodeIds: string[] = this.normalizeRestrictNodeDefIds(roleDef);
			if (!tagIds.length && !nodeIds.length) return {} as TeamUserRoleDef['restrictNodeDefTagsSettings'];

			return {
				tags: tagIds.reduce((acc: TeamUserRoleDef['restrictNodeDefTagsSettings']['tags'], id: string) => ({
					...acc,
					[id]: true,
				}), {}),
				tagTypes: { [RUNNIT_NODE_DEF_TAG_TYPE.MODEL_FAMILY]: true },
				nodeDefIds: nodeIds,
			} as TeamUserRoleDef['restrictNodeDefTagsSettings'];
		},
		normalizePluginAccessPayload (roleDef: TeamUserRoleDef | null): TeamUserRoleDef['pluginAccessSettings'] {
			// optInAll (opt-in-all) supersedes the explicit list — emit just the flag (pluginKinds wiped).
			if (roleDef?.pluginAccessSettings?.optInAll) {
				return { optInAll: true, pluginKinds: {} } as TeamUserRoleDef['pluginAccessSettings'];
			}
			// Drop falsy values + unknown plugin kinds so the persisted shape
			// stays {{ pluginKinds: { photoshop: true, ... } }} exactly.
			const raw: Record<string, unknown> = roleDef?.pluginAccessSettings?.pluginKinds || {};
			const valid = new Set<string>(Object.values(PLUGIN_KIND));
			const pluginKinds: Record<string, true> = {};
			Object.keys(raw)
				.filter((k: string) => raw[k] === true && valid.has(k))
				.sort()
				.forEach((k: string) => { pluginKinds[k] = true; });
			if (!Object.keys(pluginKinds).length) return {} as TeamUserRoleDef['pluginAccessSettings'];
			return { pluginKinds } as TeamUserRoleDef['pluginAccessSettings'];
		},
		onPluginKindToggle (pluginKind: PluginKind, checked: boolean) {
			const current: Record<string, true> = {
				...(this.editingRoleDef?.pluginAccessSettings?.pluginKinds || {}),
			} as Record<string, true>;
			if (checked) {
				current[pluginKind] = true;
			} else {
				delete current[pluginKind];
			}
			const next: TeamUserRoleDef['pluginAccessSettings'] | undefined = _isEmpty(current)
				? undefined
				: ({ pluginKinds: current } as TeamUserRoleDef['pluginAccessSettings']);
			this.editingRoleDef = {
				...this.editingRoleDef,
				pluginAccessSettings: next,
			} as TeamUserRoleDef;
		},
		pluginKindHint (pluginKind: { available: boolean; adminOnly: boolean }): string {
			if (pluginKind.adminOnly) return ' (not public yet)';
			return pluginKind.available ? '' : ' (in development)';
		},
		onPublicApiAccessToggle (checked: boolean) {
			const current = this.editingRoleDef.pluginAccessSettings || { pluginKinds: {} };
			this.editingRoleDef = {
				...this.editingRoleDef,
				pluginAccessSettings: {
					...current,
					publicApiAccess: !!checked,
				},
			} as TeamUserRoleDef;
		},
		goToPluginsPage () {
			this.routerPush(this.$route, this.$router, { name: ROUTER.PLUGINS });
		},
		onPluginAccessModeChange (mode: 'all' | 'explicit') {
			// 'all' = opt-in-all (every current + future plugin), which wipes the explicit list.
			// 'explicit' starts pre-filled with every currently-available kind so the admin
			// prunes DOWN from all, rather than opting in from nothing.
			// `publicApiAccess` is carried across, not rebuilt: it is not a plugin
			// kind, so switching between all/specific plugins must not silently
			// revoke a team's API access.
			const publicApiAccess: boolean = !!this._get(this.editingRoleDef, 'pluginAccessSettings.publicApiAccess');
			let next: TeamUserRoleDef['pluginAccessSettings'];
			if (mode === 'all') {
				next = { optInAll: true, pluginKinds: {}, publicApiAccess } as TeamUserRoleDef['pluginAccessSettings'];
			} else {
				// Deliberately the PUBLIC map, not the admin-extended one: switching to
				// explicit should never quietly grant an unreleased plugin. An RD admin ticks
				// an admin-only kind on purpose or not at all.
				const pluginKinds: Record<string, true> = {};
				Object.values(PLUGIN_KIND).forEach((kind: PluginKind) => {
					if (AVAILABLE_PLUGIN_KINDS[kind]) pluginKinds[kind] = true;
				});
				next = { optInAll: false, pluginKinds, publicApiAccess } as TeamUserRoleDef['pluginAccessSettings'];
			}
			this.editingRoleDef = {
				...this.editingRoleDef,
				pluginAccessSettings: next,
			} as TeamUserRoleDef;
		},
		onRestrictBlocklistUpdate (payload: { selectedTagIds: Record<string, true>; selectedNodeDefIds: string[] }) {
			const tagKeys: string[] = Object.keys(payload.selectedTagIds || {}).filter((k: string) => payload.selectedTagIds[k]);
			const tags: TeamUserRoleDef['restrictNodeDefTagsSettings']['tags'] = (
				tagKeys.length
					? tagKeys.reduce((acc: TeamUserRoleDef['restrictNodeDefTagsSettings']['tags'], id: string) => ({
						...acc,
						[id]: true,
					}), {})
					: {}
			) as TeamUserRoleDef['restrictNodeDefTagsSettings']['tags'];
			const nodeDefIdsRaw: string[] = (payload.selectedNodeDefIds || []).filter((id: string) => id);
			const nodeDefIds: string[] = Array.from(new Set(nodeDefIdsRaw)).sort();

			let r: TeamUserRoleDef['restrictNodeDefTagsSettings'] | undefined;
			if (_isEmpty(tags) && _isEmpty(nodeDefIds)) {
				r = undefined;
			} else {
				r = {
					tags: !_isEmpty(tags) ? tags : {},
					tagTypes: !_isEmpty(tags) ? { [RUNNIT_NODE_DEF_TAG_TYPE.MODEL_FAMILY]: true } : {},
					nodeDefIds,
				} as TeamUserRoleDef['restrictNodeDefTagsSettings'];
			}
			this.editingRoleDef = {
				...this.editingRoleDef,
				restrictNodeDefTagsSettings: r,
			} as TeamUserRoleDef;
		},
		isEnterpriseActionPreCheck () {
			const { isEnterprise } = this.companyClubOfferBenefits;
			if (!isEnterprise) {
				this.setUpsellDialog({ noTeamRoles: true });
			}
			return isEnterprise;
		},
		isPermissionDisabled (permissionKey: TeamUserRolePermission) {
			// If there is no role being edited or no dependency metadata, do not disable
			if (!this.editingRoleDef) return true;
			const permissionMeta = this.TEAM_USER_ROLE_PERMISSION_DATA(this.displayLabels)[permissionKey];
			const dependsOn = permissionMeta?.dependsOn || [];
			if (!dependsOn.length) return false;
			// Disabled if ANY dependency is not truthy in current permissions
			return dependsOn.some((depKey: TeamUserRolePermission) => {
				return !this.editingRoleDef?.permissions?.[depKey];
			});
		},
		onTokenLimitMaxInput (val: number | string | null | undefined) {
			const maxVal = Number.isFinite(val) && (val as number) > 0 ? tokensToHundredths(val as number) : undefined;
			const tls = { ...(this.editingRoleDef?.tokenLimitSettings || {}) };
			if (typeof maxVal === 'undefined') {
				delete tls.max;
			} else {
				tls.max = maxVal;
			}
			this.editingRoleDef = { ...this.editingRoleDef, tokenLimitSettings: tls } as TeamUserRoleDef;
		},
		onTokenLimitApplyToTotalRunChange (value: boolean) {
			const tls = { ...(this.editingRoleDef?.tokenLimitSettings || {}) };
			if (value === true) {
				tls.applyToTotalRun = true;
			} else {
				delete tls.applyToTotalRun;
			}
			this.editingRoleDef = { ...this.editingRoleDef, tokenLimitSettings: tls } as TeamUserRoleDef;
		},
		/**
		 * The stored shape of a role's per-media-type caps: junk dropped, and
		 * entries for tags that are not MEDIA tags dropped too.
		 *
		 * The tag allow-list is only applied once the MEDIA tags have LOADED.
		 * Filtering against an empty list mid-load would quietly strip a role's
		 * real caps and then save the stripped version.
		 */
		normalizeTokenLimitMediaTypeMaxes (roleDef: TeamUserRoleDef | null): Record<string, number> {
			const allowedTagIds: string[] | null = this.mediaTypeTagIds.length ? this.mediaTypeTagIds : null;
			return normalizeMediaTypeMaxes(roleDef?.tokenLimitSettings?.mediaTypeMaxes, allowedTagIds);
		},
		setTokenLimitMediaTypeMaxes (mediaTypeMaxes: Record<string, number>) {
			const tls = { ...(this.editingRoleDef?.tokenLimitSettings || {}) };
			if (_isEmpty(mediaTypeMaxes)) {
				delete tls.mediaTypeMaxes;
			} else {
				tls.mediaTypeMaxes = mediaTypeMaxes;
			}
			this.editingRoleDef = { ...this.editingRoleDef, tokenLimitSettings: tls } as TeamUserRoleDef;
		},
		onTokenLimitMediaModeChange (mode: 'master' | 'perMedia') {
			this.tokenLimitMediaModeSelection = mode;
			// Going back to one limit for everything CLEARS the per-media caps
			// rather than parking them: leaving them stored would mean the radio
			// said one thing and the role enforced another.
			if (mode === 'master') this.setTokenLimitMediaTypeMaxes({});
		},
		/** A media type has its own limit when the map holds a number for it, 0 included (an in-progress edit). */
		isMediaTypeLimitOn (tagId: string): boolean {
			return typeof this.storedMediaTypeLimit(tagId) === 'number';
		},
		mediaTypeLimitValue (tagId: string): number | undefined {
			const raw: number | undefined = this.storedMediaTypeLimit(tagId);
			return typeof raw === 'number' ? hundredthsToTokens(raw) : undefined;
		},
		/** Indexed rather than looked up by dotted path: the key is a document id, not a field name. */
		storedMediaTypeLimit (tagId: string): number | undefined {
			return this.editingRoleDef?.tokenLimitSettings?.mediaTypeMaxes?.[tagId];
		},
		onMediaTypeLimitToggle (tagId: string, checked: boolean) {
			const next: Record<string, number> = { ...(this.editingRoleDef?.tokenLimitSettings?.mediaTypeMaxes || {}) };
			if (checked) {
				// Seeded from the master cap so the admin adjusts the number already
				// in front of them. An empty field would read as "no limit", which is
				// the opposite of what switching this on means.
				const masterMax: number = this.editingRoleDef?.tokenLimitSettings?.max;
				next[tagId] = (typeof masterMax === 'number' && masterMax > 0) ? masterMax : 0;
			} else {
				delete next[tagId];
			}
			this.setTokenLimitMediaTypeMaxes(next);
		},
		onMediaTypeLimitInput (tagId: string, val: number | string | null | undefined) {
			const next: Record<string, number> = { ...(this.editingRoleDef?.tokenLimitSettings?.mediaTypeMaxes || {}) };
			// A cleared field stays as 0 so the row keeps its toggle on while the
			// admin types. It is dropped on save, the same way a cleared master cap
			// clears the whole setting.
			next[tagId] = (Number.isFinite(val) && (val as number) > 0) ? tokensToHundredths(val as number) : 0;
			this.setTokenLimitMediaTypeMaxes(next);
		},
		onTokenCooldownTokensInput (val: number | string | null | undefined) {
			const tokensVal = Number.isFinite(val) && (val as number) > 0 ? tokensToHundredths(val as number) : undefined;
			const tcs = { ...(this.editingRoleDef?.tokenCooldownSettings || {}) };
			if (typeof tokensVal === 'undefined') {
				delete tcs.tokens;
			} else {
				tcs.tokens = tokensVal;
			}
			if (this.editingRoleDef?.tokenCooldownSettings?.timeBucket) {
				tcs.timeBucket = this.editingRoleDef.tokenCooldownSettings.timeBucket;
			}
			this.editingRoleDef = { ...this.editingRoleDef, tokenCooldownSettings: tcs } as TeamUserRoleDef;
		},
		onTokenCooldownTimeBucketInput (value: string | null | undefined) {
			const tcs = { ...(this.editingRoleDef?.tokenCooldownSettings || {}) };
			if (!value || value === null || value === '') {
				delete tcs.timeBucket;
			} else {
				tcs.timeBucket = value as TokenCooldownTimeBucket;
			}
			// Preserve tokens if they exist
			if (this.editingRoleDef?.tokenCooldownSettings?.tokens) {
				tcs.tokens = this.editingRoleDef.tokenCooldownSettings.tokens;
			}
			this.editingRoleDef = { ...this.editingRoleDef, tokenCooldownSettings: tcs } as TeamUserRoleDef;
		},
		onNumResultsLimitMaxInput (val: number | string | null | undefined) {
			const maxVal = Number.isFinite(val) && (val as number) > 0 ? (val as number) : undefined;
			const nrl = { ...(this.editingRoleDef?.numResultsLimitSettings || {}) };
			if (typeof maxVal === 'undefined') {
				delete nrl.max;
			} else {
				nrl.max = maxVal;
			}
			this.editingRoleDef = { ...this.editingRoleDef, numResultsLimitSettings: Object.keys(nrl).length ? nrl : undefined } as TeamUserRoleDef;
		},
		onPermissionChange (permissionKey: TeamUserRolePermission, value: boolean) {
			if (!this.editingRoleDef) return;

			// Update the permission
			this.editingRoleDef = {
				...this.editingRoleDef,
				permissions: {
					...this.editingRoleDef.permissions,
					[permissionKey]: value,
				},
			} as TeamUserRoleDef;

			// If permission is being turned off, turn off all dependent permissions
			if (!value) {
				this.turnOffDependentPermissions(permissionKey);
			}
		},
		/**
		 * Safely turns off dependent permissions with protection against circular dependencies.
		 *
		 * Protection mechanisms:
		 * 1. Depth limit (max 10 levels) - prevents stack overflow
		 * 2. Processed set tracking - detects when we've already processed a permission
		 * 3. Console warnings - alerts developers to bad data
		 * 4. Graceful exit - continues processing other permissions
		 *
		 * Example of bad data that could cause infinite loops:
		 * Permission A depends on Permission B
		 * Permission B depends on Permission C
		 * Permission C depends on Permission A (CIRCULAR!)
		 */
		turnOffDependentPermissions (parentPermissionKey: TeamUserRolePermission) {
			if (!this.editingRoleDef) return;

			const allPermissions = Object.keys(this.TEAM_USER_ROLE_PERMISSION_DATA(this.displayLabels)) as TeamUserRolePermission[];
			const permissionsToTurnOff: TeamUserRolePermission[] = [];
			const processedPermissions = new Set<TeamUserRolePermission>(); // Track processed permissions to prevent infinite loops

			// Find all permissions that depend on the parent permission
			allPermissions.forEach((permissionKey) => {
				const permissionData = this.TEAM_USER_ROLE_PERMISSION_DATA(this.displayLabels)[permissionKey];
				const dependsOn = permissionData?.dependsOn || [];

				if (dependsOn.includes(parentPermissionKey)) {
					permissionsToTurnOff.push(permissionKey);
				}
			});

			// Recursively turn off dependent permissions and their dependencies
			const turnOffRecursively = (permissionKey: TeamUserRolePermission, depth = 0) => {
				// Safety check: prevent infinite loops
				if (depth > 10) {
					console.warn(`[turnOffDependentPermissions] Maximum recursion depth reached for permission: ${permissionKey}. Possible circular dependency detected.`);
					return;
				}

				// Check if we've already processed this permission to prevent circular dependencies
				if (processedPermissions.has(permissionKey)) {
					console.warn(`[turnOffDependentPermissions] Circular dependency detected for permission: ${permissionKey}. Skipping to prevent infinite loop.`);
					return;
				}

				// Mark this permission as being processed
				processedPermissions.add(permissionKey);

				// Turn off this permission
				this.editingRoleDef = {
					...this.editingRoleDef,
					permissions: {
						...this.editingRoleDef.permissions,
						[permissionKey]: false,
					},
				};

				// Find and turn off permissions that depend on this one
				allPermissions.forEach((dependentKey) => {
					const dependentData = this.TEAM_USER_ROLE_PERMISSION_DATA(this.displayLabels)[dependentKey];
					const dependsOn = dependentData?.dependsOn || [];

					if (dependsOn.includes(permissionKey) && this.editingRoleDef?.permissions?.[dependentKey]) {
						turnOffRecursively(dependentKey, depth + 1);
					}
				});
			};

			// Turn off all dependent permissions recursively
			permissionsToTurnOff.forEach((permissionKey) => {
				// Reset processed set for each top-level permission to allow proper dependency chains
				processedPermissions.clear();
				turnOffRecursively(permissionKey);
			});
		},
		goBack () {
			// Go through breadcrumbs in reverse order to find the back route
			for (let i = this.breadcrumbs.length - 1; i >= 0; i--) {
				const breadcrumb = this.breadcrumbs[i];
				if (breadcrumb.to && !breadcrumb.disabled) {
					this.$router.push(breadcrumb.to);
					return;
				}
			}
		},
		async bindTeamUserRoleDefs () {
			this.loadingAvailableRoleDefs = true;
			try {
				const companyId = this.team?.companyId;
				if (!companyId) {
					console.error('No company ID found for user');
					return;
				}

				// index created: teamUserRoleDefs: companyId Ascending isDeleted Ascending level Descending __name__ Descending
				const roleDefsRef = db
					.collection('teamUserRoleDefs')
					.where('companyId', '==', companyId)
					.where('isDeleted', '==', false)
					.orderBy('level', 'desc');

				await this.$bind(
					'availableRoleDefs',
					roleDefsRef,
					get$bindFirestoreOptions(),
				);
			} catch (error) {
				console.error('Error binding team user role defs:', error);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Failed to load team roles',
					show: true,
				});
			} finally {
				this.loadingAvailableRoleDefs = false;
			}
		},
		onSwitchRole (roleId: string) {
			if (!roleId) return;

			const roleDef = this.availableRoleDefs.find(r => r.id === roleId);
			if (!roleDef) return;

			this.originalRoleDef = { permissions: {}, tokenLimitSettings: {}, tokenCooldownSettings: {}, numResultsLimitSettings: {}, restrictNodeDefTagsSettings: {}, pluginAccessSettings: {}, ..._cloneDeep(roleDef), id: roleDef.id };
			this.editingRoleDef = { permissions: {}, tokenLimitSettings: {}, tokenCooldownSettings: {}, numResultsLimitSettings: {}, restrictNodeDefTagsSettings: {}, pluginAccessSettings: {}, ..._cloneDeep(roleDef), id: roleDef.id };
			this.initializeRolePermissionKeys();
			// Re-derive the token-limit media mode from the role we just loaded.
			this.tokenLimitMediaModeSelection = null;
			this.isEditMode = false;
		},
		enterEditMode () {
			if (!this.isEnterpriseActionPreCheck()) return;
			if (!this.editingRoleDef) return;

			if (!this.originalRoleDef) {
				this.originalRoleDef = { permissions: {}, tokenLimitSettings: {}, tokenCooldownSettings: {}, numResultsLimitSettings: {}, restrictNodeDefTagsSettings: {}, pluginAccessSettings: {}, ..._cloneDeep(this.editingRoleDef), id: this.editingRoleDef.id };
			}
			this.isEditMode = true;
		},
		exitEditMode () {
			this.isEditMode = false;
			this.editingRoleDef = { permissions: {}, tokenLimitSettings: {}, tokenCooldownSettings: {}, numResultsLimitSettings: {}, restrictNodeDefTagsSettings: {}, pluginAccessSettings: {}, ..._cloneDeep(this.originalRoleDef), id: this.originalRoleDef.id };
			this.tokenLimitMediaModeSelection = null;
		},
		onClearRole () {
			this.originalRoleDef = null;
			this.editingRoleDef = null;
			this.isEditMode = false;
			this.tokenLimitMediaModeSelection = null;
		},
		async saveExistingRole () {
			if (!this.isEnterpriseActionPreCheck()) return;

			this.saving = true;
			try {
				const roleDefId: string = this.editingRoleDef?.id;
				if (!roleDefId) {
					throw new Error('Missing role definition id');
				}

				const updates: any = {};
				// Basic fields
				if (this.editingRoleDef.name !== this.originalRoleDef.name) {
					updates.name = (this.editingRoleDef.name || '').trim();
				}
				if ((this.editingRoleDef.description || '') !== (this.originalRoleDef.description || '')) {
					updates.description = (this.editingRoleDef.description || '').trim();
				}
				if ((this.editingRoleDef.color || '') !== (this.originalRoleDef.color || '')) {
					updates.color = (this.editingRoleDef.color || '').trim();
				}
				if (this.editingRoleDef.level !== this.originalRoleDef.level) {
					updates.level = this.editingRoleDef.level;
				}

				// Permissions (send only true keys)
				const getTrueKeys = (perms?: Record<string, boolean>) => {
					if (!perms || Object.keys(perms).length === 0) return [] as string[];
					return Object.keys(perms).filter((k) => !!perms[k]).sort();
				};
				const origTrue = getTrueKeys(this.originalRoleDef.permissions);
				const editTrue = getTrueKeys(this.editingRoleDef.permissions);
				if (JSON.stringify(origTrue) !== JSON.stringify(editTrue)) {
					const filtered: Record<string, true> = {};
					editTrue.forEach((k) => { filtered[k] = true; });
					updates.permissions = filtered;
				}

				// Token limit
				const origMax = this.originalRoleDef.tokenLimitSettings?.max;
				const origApplyToTotalRun = this.originalRoleDef.tokenLimitSettings?.applyToTotalRun;
				const maxRaw: any = this.editingRoleDef?.tokenLimitSettings?.max;
				const editApplyToTotalRun = this.editingRoleDef?.tokenLimitSettings?.applyToTotalRun;
				const maxNum = maxRaw === '' || maxRaw === null || typeof maxRaw === 'undefined' ? undefined : Number(maxRaw);
				const normOrigMax = (typeof origMax === 'number' && Number.isFinite(origMax) && origMax > 0) ? origMax : undefined;
				const normEditMax = (typeof maxNum === 'number' && Number.isFinite(maxNum) && maxNum > 0) ? maxNum : undefined;
				const normOrigApplyToTotalRun = origApplyToTotalRun === true;
				const normEditApplyToTotalRun = editApplyToTotalRun === true;

				const origMediaTypeMaxes = this.normalizeTokenLimitMediaTypeMaxes(this.originalRoleDef);
				const editMediaTypeMaxes = this.normalizeTokenLimitMediaTypeMaxes(this.editingRoleDef);
				const mediaTypeMaxesChanged: boolean = JSON.stringify(origMediaTypeMaxes) !== JSON.stringify(editMediaTypeMaxes);

				if (normOrigMax !== normEditMax || normOrigApplyToTotalRun !== normEditApplyToTotalRun || mediaTypeMaxesChanged) {
					if (Number.isFinite(maxNum)) {
						const tls: TeamUserRoleDef['tokenLimitSettings'] = { max: maxNum as number };
						if (normEditApplyToTotalRun) {
							tls.applyToTotalRun = true;
						}
						// The callable REPLACES the stored tokenLimitSettings, so omitting
						// this key when there are no caps is what clears them.
						if (!_isEmpty(editMediaTypeMaxes)) {
							tls.mediaTypeMaxes = editMediaTypeMaxes;
						}
						updates.tokenLimitSettings = tls;
					} else {
						updates.tokenLimitSettings = {};
					}
				}

				// Token cooldown
				const origTokens = this.originalRoleDef.tokenCooldownSettings?.tokens;
				const origTimeBucket = this.originalRoleDef.tokenCooldownSettings?.timeBucket;
				const tokensRaw: any = this.editingRoleDef?.tokenCooldownSettings?.tokens;
				const editTimeBucket = this.editingRoleDef?.tokenCooldownSettings?.timeBucket;
				const tokensNum = tokensRaw === '' || tokensRaw === null || typeof tokensRaw === 'undefined' ? undefined : Number(tokensRaw);
				const normOrigTokens = (typeof origTokens === 'number' && Number.isFinite(origTokens)) ? origTokens : undefined;
				const normEditTokens = (typeof tokensNum === 'number' && Number.isFinite(tokensNum)) ? tokensNum : undefined;
				const normOrigTimeBucket = origTimeBucket || undefined;
				const normEditTimeBucket = editTimeBucket || normOrigTimeBucket || undefined;

				if (normOrigTokens !== normEditTokens || normOrigTimeBucket !== normEditTimeBucket) {
					const hasValidTokens = Number.isFinite(tokensNum) && (tokensNum as number) > 0;
					const hasValidTimeBucket = Object.values(TOKEN_COOLDOWN_TIME_BUCKET).includes(normEditTimeBucket as TokenCooldownTimeBucket);
					if (hasValidTokens && hasValidTimeBucket) {
						updates.tokenCooldownSettings = {
							tokens: tokensNum as number,
							timeBucket: normEditTimeBucket,
						};
					} else if (!hasValidTokens && !hasValidTimeBucket) {
						updates.tokenCooldownSettings = {};
					} else {
						const fallbackTokens = hasValidTokens ? (tokensNum as number) : (typeof origTokens === 'number' && Number.isFinite(origTokens) && origTokens > 0 ? origTokens : undefined);
						const fallbackTimeBucket = hasValidTimeBucket ? normEditTimeBucket : (normOrigTimeBucket || undefined);
						if (Number.isFinite(fallbackTokens) && (fallbackTokens as number) > 0 && fallbackTimeBucket) {
							updates.tokenCooldownSettings = { tokens: fallbackTokens as number, timeBucket: fallbackTimeBucket };
						} else {
							updates.tokenCooldownSettings = {};
						}
					}
				}

				// Num results limit
				const origNumResultsMax = this.originalRoleDef.numResultsLimitSettings?.max;
				const numResultsMaxRaw: any = this.editingRoleDef?.numResultsLimitSettings?.max;
				const numResultsMaxNum = numResultsMaxRaw === '' || numResultsMaxRaw === null || typeof numResultsMaxRaw === 'undefined' ? undefined : Number(numResultsMaxRaw);
				const normOrigNumResultsMax = (typeof origNumResultsMax === 'number' && Number.isFinite(origNumResultsMax) && origNumResultsMax > 0) ? origNumResultsMax : undefined;
				const normEditNumResultsMax = (typeof numResultsMaxNum === 'number' && Number.isFinite(numResultsMaxNum) && numResultsMaxNum > 0) ? numResultsMaxNum : undefined;

				if (normOrigNumResultsMax !== normEditNumResultsMax) {
					if (normEditNumResultsMax !== undefined) {
						updates.numResultsLimitSettings = { max: normEditNumResultsMax };
					} else {
						updates.numResultsLimitSettings = {};
					}
				}

				// Model family + per-tool block list (BLOCK_NODE_DEFS)
				const origRestrict = this.normalizeRestrictNodeDefTagsPayload(this.originalRoleDef);
				const editRestrict = this.normalizeRestrictNodeDefTagsPayload(this.editingRoleDef);
				if (JSON.stringify(origRestrict) !== JSON.stringify(editRestrict)) {
					updates.restrictNodeDefTagsSettings = Object.keys(editRestrict).length ? editRestrict : {};
				}

				// USE_PLUGINS allow list (pluginAccessSettings.pluginKinds)
				const origPlugins = this.normalizePluginAccessPayload(this.originalRoleDef);
				const editPlugins = this.normalizePluginAccessPayload(this.editingRoleDef);
				if (JSON.stringify(origPlugins) !== JSON.stringify(editPlugins)) {
					updates.pluginAccessSettings = Object.keys(editPlugins).length ? editPlugins : {};
				}

				const updateFn = functions.httpsCallable('updateTeamUserRoleDef');
				const result = await updateFn({ roleDefId, updates }) as any;
				if (result?.data?.success) {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.SUCCESS,
						message: result?.data?.message || 'Role updated',
						show: true,
					});
					this.originalRoleDef = _cloneDeep(this.editingRoleDef);
					this.isEditMode = false;
				} else {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: result?.data?.message || 'Failed to update role',
						show: true,
					});
				}
			} catch (error) {
				console.error('Error saving existing role:', error);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Failed to save role',
					show: true,
				});
			} finally {
				this.saving = false;
			}
		},
		async saveAsNewRole () {
			if (!this.isEnterpriseActionPreCheck()) return;

			this.saving = true;
			try {
				const companyId: string = this.team?.companyId;
				if (!companyId) {
					throw new Error('No company ID found for user');
				}

				const filteredPermissions = Object.entries(this.editingRoleDef?.permissions || {})
					.filter(([_, value]) => value === true)
					.reduce((acc, [key]) => {
						acc[key as TeamUserRolePermission] = true;
						return acc;
					}, {} as Record<TeamUserRolePermission, true>);

				// Coerce tokenLimitSettings.max to a number if present
				const maxRaw: any = this.editingRoleDef?.tokenLimitSettings?.max;
				const maxNum = maxRaw === '' || maxRaw === null || typeof maxRaw === 'undefined' ? undefined : Number(maxRaw);

				// tokenCooldownSettings: state is hundredths; send as-is
				const tokensRaw: any = this.editingRoleDef?.tokenCooldownSettings?.tokens;
				const tokensNum = tokensRaw === '' || tokensRaw === null || typeof tokensRaw === 'undefined' ? undefined : Number(tokensRaw);
				const timeBucket = this.editingRoleDef?.tokenCooldownSettings?.timeBucket;

				const numResultsMaxRaw: any = this.editingRoleDef?.numResultsLimitSettings?.max;
				const numResultsMaxNum = numResultsMaxRaw === '' || numResultsMaxRaw === null || typeof numResultsMaxRaw === 'undefined' ? undefined : Number(numResultsMaxRaw);

				const mediaTypeMaxesPayload = this.normalizeTokenLimitMediaTypeMaxes(this.editingRoleDef);
				const restrictPayload = this.normalizeRestrictNodeDefTagsPayload(this.editingRoleDef);
				const pluginAccessPayload = this.normalizePluginAccessPayload(this.editingRoleDef);

				const createTeamUserRoleDefFn = functions.httpsCallable('createTeamUserRoleDef');
				const roleDef = {
					companyId,
					name: this.editingRoleDef?.name || 'New Role',
					description: this.editingRoleDef?.description || '',
					color: this.editingRoleDef?.color || '#1e88e5',
					level: this.editingRoleDef?.level || 1,
					permissions: filteredPermissions,
					...(Number.isFinite(maxNum)
						? {
							tokenLimitSettings: {
								max: maxNum as number,
								// Carried across like every other token-limit field: a role
								// saved as new should enforce what the editor was showing.
								...(this.editingRoleDef?.tokenLimitSettings?.applyToTotalRun === true && { applyToTotalRun: true }),
								...(!_isEmpty(mediaTypeMaxesPayload) && { mediaTypeMaxes: mediaTypeMaxesPayload }),
							},
						}
						: {}),
					...(Number.isFinite(tokensNum) && (tokensNum as number) > 0 && timeBucket
						? { tokenCooldownSettings: { tokens: tokensNum as number, timeBucket } }
						: {}),
					...(Number.isFinite(numResultsMaxNum) && (numResultsMaxNum as number) > 0
						? { numResultsLimitSettings: { max: numResultsMaxNum as number } }
						: {}),
					...(Object.keys(restrictPayload).length > 0
						? { restrictNodeDefTagsSettings: restrictPayload as TeamUserRoleDef['restrictNodeDefTagsSettings'] }
						: {}),
					...(Object.keys(pluginAccessPayload).length > 0
						? { pluginAccessSettings: pluginAccessPayload as TeamUserRoleDef['pluginAccessSettings'] }
						: {}),
				};
				const result = await createTeamUserRoleDefFn({
					roleDef,
				}) as any;

				if (result?.data?.success) {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.SUCCESS,
						message: result?.data?.message || 'Successfully created new role',
						show: true,
					});
					this.exitEditMode();
				} else {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: result?.data?.message || 'Failed to create new role',
						show: true,
					});
				}
			} catch (error) {
				console.error('Error saving as new role:', error);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Failed to save as new',
					show: true,
				});
			} finally {
				this.saving = false;
			}
		},
		deleteRole () {
			if (!this.isEnterpriseActionPreCheck()) return;

			this.deleteConfirmDialogOpen = true;
		},
		async confirmDeleteRole (resetDialog?: () => void) {
			if (!this.isEnterpriseActionPreCheck()) return;

			this.deletingRole = true;
			try {
				const roleDefId: string = this.editingRoleDef?.id;
				if (!roleDefId) {
					throw new Error('Missing role definition id');
				}
				const deleteFn = functions.httpsCallable('deleteTeamUserRoleDef');
				const result = await deleteFn({ roleDefId }) as any;
				if (result?.data?.success) {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.SUCCESS,
						message: result?.data?.message || 'Deleted role',
						show: true,
					});
					this.onClearRole();
				} else {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: result?.data?.message || 'Failed to delete role',
						show: true,
					});
				}
			} catch (error) {
				console.error('Error deleting role:', error);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Failed to delete role',
					show: true,
				});
			} finally {
				this.deletingRole = false;
				if (resetDialog) resetDialog();
				this.deleteConfirmDialogOpen = false;
			}
		},
		resetRole () {
			this.editingRoleDef = { permissions: {}, tokenLimitSettings: {}, tokenCooldownSettings: {}, numResultsLimitSettings: {}, restrictNodeDefTagsSettings: {}, pluginAccessSettings: {}, ..._cloneDeep(this.originalRoleDef) };
			this.tokenLimitMediaModeSelection = null;
		},
		initializeRolePermissionKeys () {
			if (!this.editingRoleDef) return;
			if (!this.editingRoleDef.permissions) {
				this.$set(this.editingRoleDef, 'permissions', {} as Record<TeamUserRolePermission, true>);
			}
			const allKeys = Object.keys(this.TEAM_USER_ROLE_PERMISSION_DATA(this.displayLabels)) as TeamUserRolePermission[];
			allKeys.forEach((key: TeamUserRolePermission) => {
				if (typeof this.editingRoleDef.permissions[key] === 'undefined') {
					this.$set(this.editingRoleDef.permissions, key, false);
				}
			});
		},
		onRoleLevelInput (val: number | string | null | undefined) {
			if (!this.editingRoleDef) return;
			this.editingRoleDef = { ...this.editingRoleDef, level: typeof val === 'number' ? val : (this.editingRoleDef.level || 1) } as TeamUserRoleDef;
		},
	},
	components: {
		EmptyState,
		BackButton,
		BaseBreadcrumbs,
		BaseButton,
		BaseGreyToggleButton,
		ColorInput,
		DoubleConfirmDangerDialog,
		NumberWithIncrementer,
		NumberField,
		EnterpriseUpsellBadge,
		TokenCooldownTimeBucketPicker,
		RoleDefBadge,
		RunnitToolsListSelectByTag,
		BaseToggle,
	},
});
